/**
 * Funções de validação para o Formulário Interativo
 */

import { REGEX_PATTERNS, MESSAGES } from './constants';

export const validateEmail = (email: string): { valid: boolean; error?: string } => {
  if (!email) return { valid: false, error: MESSAGES.REQUIRED_FIELD };
  if (!REGEX_PATTERNS.EMAIL.test(email)) return { valid: false, error: MESSAGES.INVALID_EMAIL };
  return { valid: true };
};

export const validatePhone = (phone: string): { valid: boolean; error?: string } => {
  if (!phone) return { valid: false, error: MESSAGES.REQUIRED_FIELD };
  if (phone.replace(/\D/g, '').length < 10) return { valid: false, error: MESSAGES.INVALID_PHONE };
  return { valid: true };
};

export const validateCPF = (cpf: string): { valid: boolean; error?: string } => {
  if (!cpf) return { valid: false, error: MESSAGES.REQUIRED_FIELD };

  const cleanCPF = cpf.replace(/\D/g, '');

  // Validar tamanho
  if (cleanCPF.length !== 11) return { valid: false, error: MESSAGES.INVALID_CPF };

  // Validar se não é sequência repetida
  if (/^(\d)\1{10}$/.test(cleanCPF)) return { valid: false, error: MESSAGES.INVALID_CPF };

  // Validar dígitos verificadores
  let sum = 0;
  let remainder;

  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cleanCPF.substring(i - 1, i)) * (11 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.substring(9, 10)))
    return { valid: false, error: MESSAGES.INVALID_CPF };

  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cleanCPF.substring(i - 1, i)) * (12 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.substring(10, 11)))
    return { valid: false, error: MESSAGES.INVALID_CPF };

  return { valid: true };
};

export const validateCEP = (cep: string): { valid: boolean; error?: string } => {
  if (!cep) return { valid: false, error: MESSAGES.REQUIRED_FIELD };
  const cleanCEP = cep.replace(/\D/g, '');
  if (cleanCEP.length !== 8) return { valid: false, error: MESSAGES.INVALID_CEP };
  return { valid: true };
};

export const validateDate = (date: string): { valid: boolean; error?: string } => {
  if (!date) return { valid: false, error: MESSAGES.REQUIRED_FIELD };

  const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = date.match(dateRegex);

  if (!match) return { valid: false, error: MESSAGES.INVALID_DATE };

  const day = parseInt(match[1]);
  const month = parseInt(match[2]);
  const year = parseInt(match[3]);

  if (month < 1 || month > 12) return { valid: false, error: MESSAGES.INVALID_DATE };
  if (day < 1 || day > 31) return { valid: false, error: MESSAGES.INVALID_DATE };

  const dateObj = new Date(year, month - 1, day);
  if (dateObj.getFullYear() !== year || dateObj.getMonth() !== month - 1 || dateObj.getDate() !== day) {
    return { valid: false, error: MESSAGES.INVALID_DATE };
  }

  // Verificar se não é data futura
  if (dateObj > new Date()) return { valid: false, error: 'Data não pode ser no futuro' };

  return { valid: true };
};

export const validateRG = (rg: string): { valid: boolean; error?: string } => {
  if (!rg) return { valid: false, error: MESSAGES.REQUIRED_FIELD };
  const cleanRG = rg.replace(/\D/g, '');
  if (cleanRG.length < 7) return { valid: false, error: MESSAGES.INVALID_RG };
  return { valid: true };
};

export const validateInstagram = (instagram: string): { valid: boolean; error?: string } => {
  if (!instagram) return { valid: false, error: MESSAGES.REQUIRED_FIELD };
  const clean = instagram.replace('@', '');
  if (clean.length < 3 || clean.length > 30) return { valid: false, error: MESSAGES.INVALID_INSTAGRAM };
  if (!/^[a-zA-Z0-9_.-]+$/.test(clean)) return { valid: false, error: MESSAGES.INVALID_INSTAGRAM };
  return { valid: true };
};

export const validateText = (text: string, minLength: number = 2): { valid: boolean; error?: string } => {
  if (!text || text.trim().length === 0) return { valid: false, error: MESSAGES.REQUIRED_FIELD };
  if (text.trim().length < minLength) return { valid: false, error: `Mínimo de ${minLength} caracteres` };
  return { valid: true };
};

export const validateField = (
  field: string,
  value: any,
  fieldType: 'email' | 'phone' | 'cpf' | 'cep' | 'date' | 'rg' | 'instagram' | 'text' = 'text'
): { valid: boolean; error?: string } => {
  switch (fieldType) {
    case 'email':
      return validateEmail(value);
    case 'phone':
      return validatePhone(value);
    case 'cpf':
      return validateCPF(value);
    case 'cep':
      return validateCEP(value);
    case 'date':
      return validateDate(value);
    case 'rg':
      return validateRG(value);
    case 'instagram':
      return validateInstagram(value);
    case 'text':
      return validateText(value);
    default:
      return validateText(value);
  }
};

export const capitalizeEachWord = (str: string): string => {
  return str
    .split(/\s+/)
    .map(word => (word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(' ');
}